import type { SVGProps } from 'react';

/**
 * The Campaigns home's first-visit illustrations. Drawn on the console's own
 * tokens rather than fixed colours — `--c-primary`, `--c-card` and the `--ill-*`
 * set in comms.css — so they turn with the theme instead of sitting as a white
 * card on a dark page. Decorative: every one is `aria-hidden` unless given a
 * title by the caller.
 */
const frame = (props: SVGProps<SVGSVGElement>) => ({
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 240 160',
  fill: 'none',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

/** An envelope with a letter, and a paper plane flying off. */
export function CampaignsArt(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...frame(props)}>
      <ellipse cx="120" cy="90" rx="100" ry="62" fill="var(--c-primary-muted)" />
      <ellipse cx="112" cy="138" rx="58" ry="6" fill="var(--ill-shadow)" />
      <path d="M138 30 C152 18 166 20 178 32" stroke="var(--c-primary)" strokeWidth="2" strokeDasharray="3 5" />
      <path d="M178 34 L210 20 L198 54 L189 43 Z" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M189 43 L210 20" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="64" y="60" width="96" height="66" rx="8" fill="var(--c-primary)" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="74" y="38" width="76" height="50" rx="4" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M84 50 H128" stroke="var(--c-primary)" strokeWidth="3" />
      <path d="M84 59 H140" stroke="var(--ill-glass)" strokeWidth="3" />
      <path d="M84 67 H134" stroke="var(--ill-glass)" strokeWidth="3" />
      <path d="M64 74 L112 104 L160 74 V118 A8 8 0 0 1 152 126 H72 A8 8 0 0 1 64 118 Z" fill="var(--ill-primary-2)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M64 124 L100 96" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M160 124 L124 96" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M46 40 L47.5 44.5 L52 46 L47.5 47.5 L46 52 L44.5 47.5 L40 46 L44.5 44.5 Z" fill="var(--ill-spark)" />
      <path d="M196 92 L197.2 95.8 L201 97 L197.2 98.2 L196 102 L194.8 98.2 L191 97 L194.8 95.8 Z" fill="var(--ill-spark)" />
      <circle cx="178" cy="118" r="3" fill="var(--c-primary)" />
      <circle cx="54" cy="106" r="2.5" fill="var(--c-primary)" />
    </svg>
  );
}

/** A car, and a tick — after a trip. */
export function TripArt(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...frame(props)}>
      <ellipse cx="120" cy="88" rx="100" ry="62" fill="var(--c-primary-muted)" />
      <path d="M22 134 H218" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M40 142 H60 M84 142 H104 M128 142 H148 M172 142 H192" stroke="var(--ill-glass)" strokeWidth="3" />
      <path d="M36 112 L50 88 Q55 80 64 80 L152 80 Q162 80 170 88 L186 102 Q204 104 204 114 V122 H36 Z" fill="var(--c-primary)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M64 86 L56 102 H100 V86 Z" fill="var(--ill-glass)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M106 86 V102 H170 L156 89 Q153 86 148 86 Z" fill="var(--ill-glass)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M190 110 H200" stroke="var(--ill-light)" strokeWidth="4" />
      <circle cx="74" cy="122" r="13" fill="var(--ill-ink)" />
      <circle cx="74" cy="122" r="5" fill="var(--c-card)" />
      <circle cx="168" cy="122" r="13" fill="var(--ill-ink)" />
      <circle cx="168" cy="122" r="5" fill="var(--c-card)" />
      <circle cx="188" cy="50" r="19" fill="var(--ill-ok)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M179 50 L186 57 L198 44" stroke="var(--c-card)" strokeWidth="3.5" />
      <path d="M40 46 L41.5 50.5 L46 52 L41.5 53.5 L40 58 L38.5 53.5 L34 52 L38.5 50.5 Z" fill="var(--ill-spark)" />
      <circle cx="150" cy="40" r="2.5" fill="var(--c-primary)" />
    </svg>
  );
}

/** A first booking. */
export function FirstBookingArt(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...frame(props)}>
      <ellipse cx="120" cy="88" rx="100" ry="62" fill="var(--c-primary-muted)" />
      <rect x="70" y="44" width="100" height="90" rx="10" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M70 54 A10 10 0 0 1 80 44 H160 A10 10 0 0 1 170 54 V68 H70 Z" fill="var(--c-primary)" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="88" y="34" width="7" height="18" rx="3.5" fill="var(--ill-ink)" />
      <rect x="145" y="34" width="7" height="18" rx="3.5" fill="var(--ill-ink)" />
      <rect x="82" y="78" width="12" height="10" rx="2" fill="var(--ill-line)" />
      <rect x="146" y="78" width="12" height="10" rx="2" fill="var(--ill-line)" />
      <rect x="82" y="114" width="12" height="10" rx="2" fill="var(--ill-line)" />
      <rect x="146" y="114" width="12" height="10" rx="2" fill="var(--ill-line)" />
      <path d="M120 80 L125 93.1 L139 93.8 L128.1 102.6 L131.8 116.2 L120 108.5 L108.2 116.2 L111.9 102.6 L101 93.8 L115 93.1 Z" fill="var(--ill-spark)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M44 62 L45.5 66.5 L50 68 L45.5 69.5 L44 74 L42.5 69.5 L38 68 L42.5 66.5 Z" fill="var(--c-primary)" />
      <path d="M196 96 L197.5 100.5 L202 102 L197.5 103.5 L196 108 L194.5 103.5 L190 102 L194.5 100.5 Z" fill="var(--ill-spark)" />
      <circle cx="190" cy="56" r="3" fill="var(--c-primary)" />
    </svg>
  );
}

/** A customer gone quiet. */
export function QuietArt(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...frame(props)}>
      <ellipse cx="120" cy="88" rx="100" ry="62" fill="var(--c-primary-muted)" />
      <path d="M162 26 A26 26 0 1 0 188 62 A20 20 0 1 1 162 26 Z" fill="var(--ill-light)" stroke="var(--ill-ink)" strokeWidth="2" />
      <circle cx="120" cy="34" r="2.5" fill="var(--c-primary)" />
      <circle cx="204" cy="86" r="2.5" fill="var(--c-primary)" />
      <circle cx="140" cy="60" r="2" fill="var(--c-primary)" />
      <path d="M52 36 H64 L52 48 H64" stroke="var(--c-primary)" strokeWidth="2.5" />
      <path d="M72 52 H81 L72 61 H81" stroke="var(--c-primary)" strokeWidth="2.5" />
      <path d="M88 64 H95 L88 71 H95" stroke="var(--c-primary)" strokeWidth="2.5" />
      <path d="M30 134 H210" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M48 116 L58 98 Q62 92 69 92 L134 92 Q142 92 148 98 L160 110 Q174 112 174 120 V126 H48 Z" fill="var(--ill-glass)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M70 97 L64 108 H96 V97 Z" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M101 97 V108 H148 L138 99 Q136 97 132 97 Z" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <circle cx="76" cy="126" r="10" fill="var(--ill-ink)" />
      <circle cx="76" cy="126" r="4" fill="var(--c-card)" />
      <circle cx="148" cy="126" r="10" fill="var(--ill-ink)" />
      <circle cx="148" cy="126" r="4" fill="var(--c-card)" />
      <path d="M198 108 C198 102 206 100 208 106 C210 100 218 102 218 108 C218 116 208 120 208 122 C208 120 198 116 198 108 Z" fill="var(--ill-spark)" stroke="var(--ill-ink)" strokeWidth="2" />
    </svg>
  );
}

/** A flow: a lightning bolt, then a clock, then an envelope — something happens, a wait, a message. */
export function AutomationsArt(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...frame(props)}>
      <ellipse cx="120" cy="84" rx="104" ry="62" fill="var(--c-primary-muted)" />
      <path d="M70 80 H96 M144 80 H170" stroke="var(--ill-ink)" strokeWidth="2" strokeDasharray="3 5" />
      <path d="M90 74 L97 80 L90 86 M164 74 L171 80 L164 86" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="26" y="56" width="46" height="48" rx="12" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M53 64 L40 82 H50 L46 96 L60 76 H50 Z" fill="var(--ill-spark)" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="98" y="56" width="46" height="48" rx="12" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <circle cx="121" cy="80" r="14" fill="var(--ill-glass)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M121 72 V80 L127 84" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="170" y="56" width="46" height="48" rx="12" fill="var(--c-primary)" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="180" y="70" width="26" height="20" rx="3" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M180 73 L193 82 L206 73" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M121 116 V128 H193 V104" stroke="var(--ill-glass)" strokeWidth="2" />
      <path d="M49 34 L50.5 38.5 L55 40 L50.5 41.5 L49 46 L47.5 41.5 L43 40 L47.5 38.5 Z" fill="var(--c-primary)" />
      <circle cx="200" cy="38" r="3" fill="var(--ill-spark)" />
    </svg>
  );
}

/** A globe linked to an address bar carrying your own domain, with a tick — the link domain. */
export function LinkDomainArt(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...frame(props)}>
      <ellipse cx="120" cy="86" rx="100" ry="62" fill="var(--c-primary-muted)" />
      <circle cx="84" cy="58" r="28" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <ellipse cx="84" cy="58" rx="12" ry="28" fill="var(--ill-glass)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M56 58 H112 M60 44 H108 M60 72 H108" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="128" y="40" width="38" height="18" rx="9" transform="rotate(-28 147 49)" stroke="var(--c-primary)" strokeWidth="5" />
      <rect x="150" y="54" width="38" height="18" rx="9" transform="rotate(-28 169 63)" stroke="var(--ill-ink)" strokeWidth="5" />
      <rect x="34" y="100" width="172" height="32" rx="16" fill="var(--c-card)" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="48" y="110" width="10" height="11" rx="2" fill="var(--ill-ink)" />
      <path d="M50 110 V106 A3 3 0 0 1 56 106 V110" stroke="var(--ill-ink)" strokeWidth="2" />
      <rect x="66" y="111" width="46" height="9" rx="4.5" fill="var(--c-primary)" />
      <rect x="116" y="111" width="50" height="9" rx="4.5" fill="var(--ill-glass)" />
      <circle cx="188" cy="116" r="10" fill="var(--ill-ok)" stroke="var(--ill-ink)" strokeWidth="2" />
      <path d="M183 116 L187 120 L193 112" stroke="#ffffff" strokeWidth="2.5" />
      <path d="M200 30 L201.5 34.5 L206 36 L201.5 37.5 L200 42 L198.5 37.5 L194 36 L198.5 34.5 Z" fill="var(--ill-spark)" />
    </svg>
  );
}
